export type PractitionerEligibilityMode = 'all' | 'selected';
export type ResourceEligibilityMode = 'any' | 'selected';

export function parsePractitionerEligibility(
  value: string | null | undefined,
): PractitionerEligibilityMode {
  return value === 'selected' ? 'selected' : 'all';
}

export function parseResourceEligibility(
  value: string | null | undefined,
): ResourceEligibilityMode {
  return value === 'selected' ? 'selected' : 'any';
}

export function isPractitionerEligibleForService(
  mode: PractitionerEligibilityMode,
  selectedPractitionerIds: readonly string[],
  practitionerId: string | null | undefined,
): boolean {
  if (!practitionerId) return true;
  switch (mode) {
    case 'all':
      return true;
    case 'selected':
      return selectedPractitionerIds.includes(practitionerId);
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}

export function filterPractitionersForService<T>(
  practitioners: T[],
  mode: PractitionerEligibilityMode,
  selectedIds: readonly string[],
  idOf: (item: T) => string,
): T[] {
  switch (mode) {
    case 'all':
      return practitioners;
    case 'selected': {
      const allowed = new Set(selectedIds);
      return practitioners.filter((item) => allowed.has(idOf(item)));
    }
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}

/** null/undefined eligible ids = all clinicians (unrestricted service). */
export function cliniciansForBookableService<T extends { id: string }>(
  clinicians: T[],
  eligiblePractitionerIds: string[] | null | undefined,
): T[] {
  if (eligiblePractitionerIds == null) return clinicians;
  const allowed = new Set(eligiblePractitionerIds);
  return clinicians.filter((clinician) => allowed.has(clinician.id));
}

/**
 * "First available practitioner" means the first available practitioner who
 * is eligible for THIS service — never the first generally available clinic member.
 */
export function firstAvailablePractitionerIds(
  visiblePractitionerIds: readonly string[],
  eligiblePractitionerIds: string[] | null | undefined,
): string[] {
  if (eligiblePractitionerIds == null) return [...visiblePractitionerIds];
  const allowed = new Set(eligiblePractitionerIds);
  return visiblePractitionerIds.filter((id) => allowed.has(id));
}

/** Eligible resources for one service requirement, before availability. */
export function eligibleResourceIdsForRequirement(
  groupResourceIds: readonly string[],
  mode: ResourceEligibilityMode,
  selectedResourceIds: readonly string[],
): string[] {
  switch (mode) {
    case 'any':
      return [...groupResourceIds];
    case 'selected': {
      const allowed = new Set(selectedResourceIds);
      return groupResourceIds.filter((id) => allowed.has(id));
    }
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}

/**
 * Staff "Ny aftale" resource selectors are per selected service and per group.
 * Without a service, do not show clinic-wide requirements (that duplicates groups
 * that several services share, e.g. two "Maskiner" rows).
 */
export function resourceRequirementsForAppointmentService<
  T extends { resource_group_id: string },
>(
  serviceId: string | null | undefined,
  requirements: readonly T[] | null | undefined,
): T[] {
  if (!serviceId) return [];
  const seen = new Set<string>();
  const unique: T[] = [];
  for (const requirement of requirements ?? []) {
    if (seen.has(requirement.resource_group_id)) continue;
    seen.add(requirement.resource_group_id);
    unique.push(requirement);
  }
  return unique;
}

export function preferredResourceIdsForMove(
  currentResourceIds: string[],
  destinationResourceId: string,
  destinationGroupId: string | undefined,
  resourceGroupById: Map<string, string>,
): string[] {
  if (!destinationResourceId) return currentResourceIds;
  const withoutSameGroup = currentResourceIds.filter((id) => {
    if (id === destinationResourceId) return false;
    if (!destinationGroupId) return true;
    return resourceGroupById.get(id) !== destinationGroupId;
  });
  return [...withoutSameGroup, destinationResourceId];
}
